import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom';
import { HiCheck, HiOutlineClock, HiX, HiOutlineEmojiSad } from 'react-icons/hi';
import { FaWhatsapp } from 'react-icons/fa';
import OrderService from '../service/OrderService';
import '../css/checkout.css'
import { formatRupee } from '../components/Price';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import { notifyCartUpdated } from '../util/cartEvents';
import { whatsappLink } from '../brand';

const SUPPORT_WHATSAPP = whatsappLink()

const METHOD_LABELS = { UPI: 'UPI', CARD: 'Credit / Debit card', WHATSAPP: 'WhatsApp' }

const STATES = {
    PAID: {
        tone: 'success', icon: <HiCheck />, title: 'Order confirmed',
        text: 'Thank you! Your payment was successful and your order is confirmed. We have emailed you the details.'
    },
    AWAITING_VERIFICATION: {
        tone: 'waiting', icon: <HiOutlineClock />, title: 'Payment submitted',
        text: 'Thanks! We are verifying your payment, usually right away and always within 12 hours. Your items are held for you, and we will confirm the order as soon as it is verified.'
    },
    FAILED: {
        tone: 'failed', icon: <HiX />, title: 'Payment not completed',
        text: 'The payment did not go through, or it was cancelled. If money was deducted, it is refunded automatically, or contact us on WhatsApp.'
    },
    PENDING: {
        tone: 'waiting', icon: <HiOutlineClock />, title: 'Waiting for payment',
        text: 'We have not received this payment yet. You can go back to checkout to finish paying.'
    }
}

// Shared confirmation page for every payment method (card, UPI, WhatsApp).
const PaymentStatus = () => {
    const history = useHistory();
    const { orderId } = useParams();

    const [status, setStatus] = useState(null)
    const [loadError, setLoadError] = useState(false)

    const load = () => {
        setLoadError(false)
        OrderService.getPaymentStatus(orderId).then(res => {
            setStatus(res.data)
            if (res.data.state === 'PAID' || res.data.state === 'AWAITING_VERIFICATION') {
                notifyCartUpdated()
            }
        }).catch(error => {
            if (error?.response?.status === 401) {
                history.push('/login')
                return
            }
            setLoadError(true)
        });
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    if (loadError) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not find this payment'
                    text='Please check your orders, or try again.' actionLabel='Try again' onAction={load} />
            </main>
        )
    }

    if (!status) {
        return (
            <main className='mv-page' aria-busy='true'>
                <Skeleton height={320} radius='14px' />
            </main>
        )
    }

    const view = STATES[status.state] || STATES.PENDING
    const retry = status.state === 'FAILED' || status.state === 'PENDING'

    return (
        <main className='mv-page'>
            <section className='mv-status-card' aria-live='polite'>
                <span className={`mv-status-icon ${view.tone}`} aria-hidden='true'>{view.icon}</span>
                <h1 className='mv-status-title'>{view.title}</h1>
                <p className='mv-status-text'>{view.text}</p>

                <dl className='mv-status-details'>
                    <div className='mv-status-row'><dt>Order</dt><dd className='mv-cell-mono'>{status.orderId}</dd></div>
                    <div className='mv-status-row'><dt>Amount</dt><dd>{formatRupee(status.amount)}</dd></div>
                    {status.method &&
                        <div className='mv-status-row'><dt>Paid with</dt><dd>{METHOD_LABELS[status.method] || status.method}</dd></div>}
                </dl>

                <div className='mv-pay-actions'>
                    {retry ?
                        <button type='button' className='mv-btn mv-btn-primary mv-btn-block' onClick={() => history.push('/payment')}>
                            Try again
                        </button> :
                        <button type='button' className='mv-btn mv-btn-primary mv-btn-block' onClick={() => history.push('/orders')}>
                            View my orders
                        </button>}
                    <button type='button' className='mv-btn mv-btn-secondary mv-btn-block' onClick={() => history.push('/')}>
                        Continue shopping
                    </button>
                </div>

                <a className='mv-help-link' href={SUPPORT_WHATSAPP} target='_blank' rel='noopener noreferrer'>
                    <FaWhatsapp /> Questions? WhatsApp us
                </a>
            </section>
        </main>
    )
}

export default PaymentStatus
